import React from "react";
import { ExternalLink } from "lucide-react";
import { withTracking } from "../../config/affiliateLinks";

/**
 * AffiliateCard — branded card for an affiliate or ARC product.
 *
 * Two display modes:
 *   - "full" (default) — rich card with description + Swami's note
 *   - "compact" — small inline card for contextual placement in tabs
 *
 * Props:
 *   link       — entry from AFFILIATE_LINKS or ARC_PRODUCTS
 *   medium     — UTM medium (where the click came from), e.g. "wallets-tab"
 *   variant    — "full" | "compact"
 *   featured   — boolean (gold border, larger)
 */
const AffiliateCard = ({
  link,
  medium = "tools-tab",
  variant = "full",
  featured = false,
}) => {
  if (!link || link.skip) return null;

  const trackedUrl = withTracking(link.url, medium);
  const isCompact = variant === "compact";

  const cardClass = featured
    ? "rounded-2xl border-2 border-arc-gold bg-gradient-to-br from-arc-gold/10 to-arc-deep/5"
    : "rounded-xl border border-arc-line bg-white/[0.02] hover:border-arc-gold/40 hover:bg-white/[0.04] transition-all";

  return (
    <a
      href={trackedUrl}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className={`btn-tactile group block ${cardClass} ${isCompact ? "p-3" : "p-4"}`}
    >
      <div className={`flex ${isCompact ? "items-center gap-3" : "items-start gap-4"}`}>
        {/* Brand-colored logo tile with the company's first letter */}
        <span
          className={`flex-shrink-0 flex items-center justify-center font-bold text-white rounded-xl shadow-lg ${
            isCompact ? "w-10 h-10 text-base" : "w-12 h-12 text-lg"
          }`}
          style={{
            backgroundColor: link.brandColor || "#666",
            // Lighter text on dark brand colors, otherwise white is fine
            color: ["#000000", "#1B1B1B", "#103F68"].includes(link.brandColor)
              ? "#fff"
              : "#fff",
          }}
        >
          {link.iconLetter || link.name?.[0] || "?"}
        </span>

        {/* Body */}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2 mb-1">
            <p className={`font-semibold text-arc-ink ${isCompact ? "text-sm" : "text-base"}`}>
              {link.name}
            </p>
            {link.badge && (
              <span className="text-[9px] font-mono uppercase tracking-[0.14em] text-arc-bg px-1.5 py-0.5 rounded-md bg-arc-gold flex-shrink-0">
                {link.badge}
              </span>
            )}
          </div>
          {!isCompact && link.tagline && (
            <p className="text-xs text-arc-muted mb-2 font-mono uppercase tracking-[0.12em]">
              {link.tagline}
            </p>
          )}
          {!isCompact && link.description && (
            <p className="text-sm text-arc-muted leading-relaxed mb-2">
              {link.description}
            </p>
          )}
          {!isCompact && link.swamiNote && (
            <p className="text-xs text-arc-light italic border-l-2 border-arc-gold/40 pl-2 mb-2">
              Swami: {link.swamiNote}
            </p>
          )}
          {isCompact && link.tagline && (
            <p className="text-[11px] text-arc-dim truncate">{link.tagline}</p>
          )}

          {/* CTA / price tag row */}
          <div className="flex items-center justify-between gap-2 mt-2">
            <span
              className={`text-xs font-mono uppercase tracking-[0.14em] text-arc-gold group-hover:text-arc-light transition-colors ${
                isCompact ? "text-[10px]" : ""
              }`}
            >
              {link.cta || "Get started"} →
            </span>
            {link.priceTag && (
              <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-arc-dim">
                {link.priceTag}
              </span>
            )}
            {!link.priceTag && !isCompact && (
              <ExternalLink size={12} className="text-arc-dim group-hover:text-arc-gold transition-colors" />
            )}
          </div>
        </div>
      </div>
    </a>
  );
};

export default AffiliateCard;
